import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { ActivityItem as ActivityItemRow } from '../../components/activity/ActivityItem';
import { Skeleton } from '../../components/ui';
import { useInfiniteActivity } from '../../hooks/useInfiniteActivity';
import { formatUsdSigned } from '../../lib/format';
import type { ActivityItem, ActivityType } from '../../lib/types';
import { CabEmpty, Panel } from '../parts';
import { CabTabs } from '../tabs';

const TYPES: ActivityType[] = ['all', 'trades', 'wallet'];

function dayLabel(iso: string, locale: string): string {
  const d = new Date(iso);
  const today = new Date();
  const yest = new Date();
  yest.setDate(today.getDate() - 1);
  const sameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();
  if (sameDay(d, today)) return locale.startsWith('ru') ? 'Сегодня' : 'Today';
  if (sameDay(d, yest)) return locale.startsWith('ru') ? 'Вчера' : 'Yesterday';
  return d.toLocaleDateString(locale, { day: 'numeric', month: 'long' });
}

export function CabActivity() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language.startsWith('ru') ? 'ru-RU' : 'en-US';
  const [type, setType] = useState<ActivityType>('all');
  const q = useInfiniteActivity({ type });
  const items = useMemo(
    () => q.data?.pages.flatMap((p) => p.items) ?? [],
    [q.data],
  );
  const counts = q.data?.pages[0]?.counts;
  const pnl = q.data?.pages[0]?.filtered_pnl;
  const pnlNum = Number(pnl ?? 0);

  // Group consecutive items by calendar day for a date header.
  const groups = useMemo(() => {
    const out: { day: string; rows: ActivityItem[] }[] = [];
    for (const it of items) {
      const day = dayLabel(it.created_at, locale);
      const last = out[out.length - 1];
      if (last && last.day === day) last.rows.push(it);
      else out.push({ day, rows: [it] });
    }
    return out;
  }, [items, locale]);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <CabTabs
          value={type}
          onChange={(v) => setType(v as ActivityType)}
          options={TYPES.map((ty) => ({
            value: ty,
            label: t(`cabinet.filter_${ty}`),
          }))}
        />
        {counts ? (
          <div className="flex items-center gap-4 text-[13px]">
            <span className="text-pb-success">
              {counts.trade_won}
              {t('cabinet.w')}
            </span>
            <span className="text-pb-danger">
              {counts.trade_lost}
              {t('cabinet.l')}
            </span>
            <span className="pb-caption">
              {t('cabinet.realized_pnl')}:{' '}
              <span
                className={clsx(
                  'pb-numeral font-semibold',
                  pnlNum > 0
                    ? 'text-pb-success'
                    : pnlNum < 0
                      ? 'text-pb-danger'
                      : 'text-tg-text',
                )}
              >
                {formatUsdSigned(pnl ?? 0)}
              </span>
            </span>
          </div>
        ) : null}
      </div>

      <Panel>
        {q.isLoading ? (
          <div className="space-y-2">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-14 rounded-pb-md" />
            ))}
          </div>
        ) : items.length ? (
          <div className="space-y-4">
            {groups.map((g) => (
              <div key={g.day}>
                <div className="pb-eyebrow mb-1 px-2">{g.day}</div>
                <div className="-mx-2 divide-y divide-pb-border-soft">
                  {g.rows.map((it) => (
                    <ActivityItemRow key={it.id} item={it} locale={locale} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <CabEmpty msg={t('cabinet.no_activity')} />
        )}

        {q.hasNextPage ? (
          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={() => q.fetchNextPage()}
              disabled={q.isFetchingNextPage}
              className="pb-focus-ring rounded-pb-md border border-pb-border-soft px-4 py-2 text-[13px] font-medium text-tg-hint hover:bg-pb-paper2 hover:text-tg-text disabled:opacity-50"
            >
              {t('cabinet.load_more')}
            </button>
          </div>
        ) : null}
      </Panel>
    </div>
  );
}
